import React, { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUserAuth } from "../context/UserAuthContext";
import { User, ArrowRight } from "lucide-react";

export default function UserLoginPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const { user, login, register } = useUserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
        toast.success(t("auth.welcome_new"));
      } else {
        await login(email, password);
        toast.success(t("auth.welcome"));
      }
      const dest = location.state?.from || "/account";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 halftone-bg opacity-30" />
      <div className="absolute top-0 left-0 right-0 h-3 diagonal-stripes" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center bg-yellow-400 text-black p-4 mb-4 brutal-shadow-black">
            <User className="w-8 h-8" strokeWidth={3} />
          </div>
          <h1 className="font-heading text-4xl uppercase text-white leading-none">
            {isRegister ? t("auth.register_title") : t("auth.login_title")}
          </h1>
        </div>

        <form onSubmit={submit} data-testid={isRegister ? "register-form" : "login-form"}
          className="bg-zinc-900 border-4 border-yellow-400 p-8 space-y-5 brutal-shadow">
          {isRegister && (
            <div>
              <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-2 block">{t("auth.name")}</label>
              <input data-testid="user-name" required value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none" />
            </div>
          )}
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-2 block">{t("auth.email")}</label>
            <input data-testid="user-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none font-mono" />
          </div>
          <div>
            <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-2 block">{t("auth.password")}</label>
            <input data-testid="user-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-zinc-950 border-2 border-zinc-800 px-4 py-3 text-white focus:border-yellow-400 focus:outline-none font-mono" />
          </div>
          <button type="submit" data-testid="user-submit" disabled={loading}
            className="w-full bg-yellow-400 text-black font-heading uppercase text-lg py-4 hover:bg-white transition flex items-center justify-center gap-2 disabled:opacity-40">
            {loading ? "..." : isRegister ? t("auth.register_btn") : t("auth.login_btn")} <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </button>
          <div className="text-center text-zinc-400 text-sm">
            {isRegister ? t("auth.have_account") : t("auth.no_account")}{" "}
            <Link to={isRegister ? "/login" : "/register"} data-testid="auth-switch"
              className="text-yellow-400 hover:underline font-bold uppercase tracking-widest text-xs">
              {isRegister ? t("auth.login") : t("auth.register")}
            </Link>
          </div>
          <Link to="/" className="block text-center text-zinc-500 hover:text-yellow-400 text-xs uppercase tracking-widest">
            ← Home
          </Link>
        </form>
      </div>
    </div>
  );
}
